import "dotenv/config";
import { Command } from "commander";
import { VERSION } from "@fathom/core";
import { existsSync, readFileSync } from "node:fs";
import { resolve } from "node:path";
import { startCommand } from "./commands/go.js";
import { pricingCommand } from "./commands/pricing.js";
import { statusCommand } from "./commands/status.js";
import { trackCommand } from "./commands/track.js";
import { reconcileCommand } from "./commands/reconcile.js";
import { calibrateCommand } from "./commands/calibrate.js";
import { spendCommand } from "./commands/spend.js";
import { importCommand } from "./commands/import.js";
import { estimateCommand } from "./commands/estimate.js";
import { intakeCommand } from "./commands/intake.js";
import { initCommand } from "./commands/init.js";
import { completionsCommand } from "./commands/completions.js";
import { doctorCommand } from "./commands/doctor.js";
import { advancedCommand } from "./commands/advanced.js";
import chalk from "chalk";
import { checkForUpdate } from "./ux/update-checker.js";

const program = new Command();

program
  .name("fathom")
  .description("Workflow intelligence platform for AI-augmented development")
  .version(VERSION);

// ── Workflow ──
program.addCommand(startCommand);

// ── Planning ──
program.addCommand(intakeCommand);
program.addCommand(estimateCommand);

// ── Building ──
program.addCommand(initCommand);

// ── Reviewing ──
program.addCommand(trackCommand);
program.addCommand(reconcileCommand);
program.addCommand(calibrateCommand);
program.addCommand(spendCommand);
program.addCommand(importCommand);

// ── Utilities ──
program.addCommand(statusCommand);
program.addCommand(pricingCommand);
program.addCommand(completionsCommand);
program.addCommand(doctorCommand);

// ── Advanced (power-user commands) ──
program.addCommand(advancedCommand);

// ── Smart zero-arg default ──
program.action(() => {
  const fathomDir = resolve(process.cwd(), ".fathom");

  if (!existsSync(fathomDir)) {
    // Uninitialized project — 3-line intro
    console.log();
    console.log(chalk.bold("  Fathom") + chalk.dim(" — AI cost intelligence for developers"));
    console.log(chalk.dim("  Know what AI will cost before you start. Track spend. Get better at predicting."));
    console.log();
    console.log(`  Get started: ${chalk.cyan("fathom init")}`);
    console.log();
    return;
  }

  // Initialized project — show brief status
  console.log();

  // Read project name from config
  let projectName = "unnamed";
  const configPath = resolve(fathomDir, "config.json");
  if (existsSync(configPath)) {
    try {
      const config = JSON.parse(readFileSync(configPath, "utf-8"));
      projectName = config.project ?? "unnamed";
    } catch { /* use default */ }
  }

  console.log(chalk.bold(`  Fathom — ${projectName}`));
  console.log(chalk.dim("  " + "─".repeat(45)));

  // Features from registry
  const registryPath = resolve(fathomDir, "registry.json");
  let featureCount = 0;
  let completeCount = 0;
  if (existsSync(registryPath)) {
    try {
      const registry = JSON.parse(readFileSync(registryPath, "utf-8"));
      featureCount = registry.features?.length ?? 0;
      completeCount = registry.features?.filter((f: any) => f.status === "complete").length ?? 0;
    } catch { /* skip */ }
  }

  // Spend from tracking summary
  let sessionCount = 0;
  let lastActivity: string | undefined;
  const summaryPath = resolve(fathomDir, "tracking", "summary.json");
  if (existsSync(summaryPath)) {
    try {
      const summary = JSON.parse(readFileSync(summaryPath, "utf-8"));
      sessionCount = summary.sessions?.length ?? 0;
      if (summary.sessions?.length > 0) {
        const last = summary.sessions[summary.sessions.length - 1];
        lastActivity = last.timestamp ?? last.startedAt ?? undefined;
      }
    } catch { /* skip */ }
  }

  // Workflow state
  const statePath = resolve(fathomDir, "workflow.json");
  let totalBudget = 0;
  let totalSpent = 0;
  if (existsSync(statePath)) {
    try {
      const state = JSON.parse(readFileSync(statePath, "utf-8"));
      totalBudget = state.totalBudget ?? 0;
      totalSpent = state.totalSpent ?? 0;
      if (state.lastSessionAt) lastActivity = state.lastSessionAt;
    } catch { /* skip */ }
  }

  if (featureCount > 0) {
    console.log(`  Features:    ${completeCount}/${featureCount} complete`);
  }
  if (sessionCount > 0) {
    console.log(`  Sessions:    ${sessionCount} tracked`);
  }
  if (totalBudget > 0) {
    console.log(`  Budget:      $${totalSpent.toFixed(2)} of $${totalBudget.toFixed(2)}`);
  }
  if (lastActivity) {
    const date = new Date(lastActivity);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    let relative: string;
    if (diffDays > 0) relative = `${diffDays}d ago`;
    else if (diffHours > 0) relative = `${diffHours}h ago`;
    else relative = "just now";
    console.log(`  Last active: ${relative}`);
  }

  if (featureCount === 0 && sessionCount === 0) {
    console.log(chalk.dim("  Project initialized — no data yet."));
  }

  console.log();
  console.log(chalk.dim("  Commands: start, estimate, track, reconcile, calibrate, status, doctor"));
  console.log(chalk.dim("  Run fathom --help for all options"));
  console.log();
});

program.parse();

// Non-blocking update check — fire and forget
checkForUpdate()
  .then((result) => {
    if (result?.updateAvailable) {
      console.error(
        chalk.yellow(`\nUpdate available: ${result.current} → ${result.latest}`),
      );
      console.error(chalk.yellow(`Run: npm i -g fathom-cli`));
    }
  })
  .catch(() => {});
